import { useEffect, useState } from "react";
import { Bell, BellOff } from "lucide-react";
import { toast } from "sonner";

import {
  isPushSupported, pushPermission, getCurrentSubscription,
  enablePush, disablePush,
} from "../../lib/push";
import { ChalkCard } from "./primitives";

// Push notifications opt-in card. Shows on More for every signed-in user.
// Hides itself entirely on devices that don't support Web Push (older iOS,
// non-PWA Safari) so we don't tease an unavailable feature.
export default function NotificationsCard() {
  const [supported, setSupported] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [perm, setPerm] = useState("default");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = isPushSupported();
      if (!ok) {
        if (!cancelled) setSupported(false);
        return;
      }
      const p = pushPermission();
      const sub = await getCurrentSubscription();
      if (cancelled) return;
      setPerm(p);
      setEnabled(Boolean(sub) && p === "granted");
    })();
    return () => { cancelled = true; };
  }, []);

  async function toggle() {
    if (busy) return;
    setBusy(true);
    try {
      if (enabled) {
        await disablePush();
        setEnabled(false);
        toast.success("Push notifications turned off.");
      } else {
        await enablePush();
        setEnabled(true);
        setPerm("granted");
        toast.success("You're all set. Your Chalk Talk goes straight to your phone now.");
      }
    } catch (e) {
      if (e.code === "PERMISSION_DENIED") {
        toast.error("Browser blocked notifications. Enable in Settings → Notifications, then try again.");
      } else if (e.code === "PUSH_UNSUPPORTED") {
        toast.error("This device doesn't support push notifications.");
      } else {
        toast.error(e.message || "Couldn't update push settings.");
      }
    } finally {
      setBusy(false);
    }
  }

  if (!supported) return null;

  const headline = enabled ? "Push notifications are on." : "Turn on push notifications.";
  const sub = enabled
    ? "Chalk Talk + emergency alerts go straight to your phone."
    : perm === "denied"
      ? "Notifications are blocked. Enable them in your browser settings, then come back."
      : "Get pinged when Coach posts. Works on Android and (installed) iOS PWAs.";

  return (
    <ChalkCard data-testid="notifications-card" onClick={perm === "denied" ? undefined : toggle}>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: enabled ? "rgba(46,125,82,0.12)" : "rgba(0,0,0,0.05)",
          color: enabled ? "var(--green-mid)" : "var(--chalk-muted)",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          {enabled ? <Bell size={18} aria-hidden="true" /> : <BellOff size={18} aria-hidden="true" />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 500, color: "var(--chalk-board)" }}>{headline}</div>
          <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 4, lineHeight: 1.4 }}>{sub}</div>
        </div>
        {perm !== "denied" && (
          <button
            type="button"
            data-testid={enabled ? "notifications-disable" : "notifications-enable"}
            onClick={(e) => { e.stopPropagation(); toggle(); }}
            disabled={busy}
            className="chalk-btn-athletic"
            style={{
              padding: "9px 14px", fontSize: 11,
              background: enabled ? "transparent" : "var(--green-mid)",
              color: enabled ? "var(--chalk-board)" : "#fff",
              border: enabled ? "1.5px solid rgba(0,0,0,0.12)" : "none",
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? "…" : enabled ? "Turn off" : "Turn on"}
          </button>
        )}
      </div>
    </ChalkCard>
  );
}
